import { Box, Collapse, List, ListItem, ListItemButton, ListItemIcon } from "@mui/material";
import { useState, type ReactNode } from "react";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import CollapseMediumIcon from "@entrilia/shared/icons/CollapseMediumIcon";
import ExpandMediumIcon from "@entrilia/shared/icons/ExpandMediumIcon";
import type { FieldTreeGroup } from "./fieldTree.ts";
import GroupFolderIcon from "./GroupFolderIcon.tsx";
import VirtualizedFieldList from "./VirtualizedFieldList.tsx";

interface Props<T> {
  groups: FieldTreeGroup<T>[];
  defaultExpanded: boolean;
  visible: boolean;
  folderLayout?: boolean;
  renderField: (field: T, groupCaption: string) => ReactNode;
}

const VirtualizedFieldTree = <T extends { name: string }>({ groups, ...props }: Props<T>) => (
  <List sx={{ p: 0 }}>
    {groups.map((group) => (
      <TreeGroup key={group.id} group={group} {...props} />
    ))}
  </List>
);

const TreeGroup = <T extends { name: string }>({
  group,
  defaultExpanded,
  visible,
  folderLayout,
  renderField,
}: Omit<Props<T>, "groups"> & { group: FieldTreeGroup<T> }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [contentVisible, setContentVisible] = useState(defaultExpanded);
  const handleToggle = () => {
    setContentVisible(true);
    setExpanded(!expanded);
  };
  const handleExited = () => setContentVisible(false);

  return (
    <>
      <ListItem
        disablePadding
        sx={{
          borderRadius: "2px",
          ...(folderLayout && { height: 28 }),
          bgcolor: expanded ? "action.hover" : undefined,
          "&:hover, &:focus-within": { bgcolor: "action.hover", "& .field-group-actions": { visibility: "visible" } },
        }}
      >
        <ListItemButton
          aria-expanded={expanded}
          onClick={handleToggle}
          sx={{
            p: folderLayout ? 0 : "4px 0",
            minWidth: 0,
            ...(folderLayout && { height: "100%" }),
            "&:hover": { bgcolor: "transparent" },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: "30px",
              ...(folderLayout && { minWidth: "20px", width: "20px", color: "secondary.main" }),
            }}
          >
            {expanded ? <CollapseMediumIcon /> : <ExpandMediumIcon />}
          </ListItemIcon>
          {folderLayout && <GroupFolderIcon open={expanded} />}
          <TypographyTooltipEllipsis
            observeContainerResize
            text={group.caption}
            tooltipProps={{ disableInteractive: true }}
            typographyProps={{ variant: "subtitle2", sx: { flexGrow: 1, minWidth: 0 } }}
          />
        </ListItemButton>
        {group.actions && (
          <Box className="field-group-actions" sx={{ display: "flex", cursor: "pointer", visibility: "hidden" }}>
            {group.actions}
          </Box>
        )}
      </ListItem>
      <Collapse in={expanded} mountOnEnter onExited={handleExited}>
        <List sx={{ p: "0 0 0 1rem", ...(folderLayout && group.fields.length > 0 && { pb: 1 }) }}>
          {group.groups.length > 0 && (
            <VirtualizedFieldTree
              groups={group.groups}
              defaultExpanded={defaultExpanded}
              visible={visible && contentVisible}
              folderLayout={folderLayout}
              renderField={renderField}
            />
          )}
          {group.fields.length > 0 && (
            <VirtualizedFieldList
              fields={group.fields}
              groupCaption={group.fieldGroupCaption ?? group.caption}
              visible={visible && contentVisible}
              renderField={renderField}
            />
          )}
        </List>
      </Collapse>
    </>
  );
};

export default VirtualizedFieldTree;
